import { useDispatch, useSelector } from "react-redux";
import { getIsLoadingDeviceTypes, getDeviceTypes, getDeviceTypesError } from "selectors/deviceTypesSelectors";
import { useEffect, useMemo, useState } from "react";
import { getDeviceTypesRequest } from "actions/deviceTypeActions";
import { sortNamesBy } from "logic/sortNames";
import { DeviceClass, DeviceFamily } from 'shared/models';
import { useSession } from "hooks";

export type Options = {
    // Determines whether the device types should be refreshed from the server
    // when the calling component is mounted. Should almost always be false.
    refresh?: boolean;

    // Only return device types of the specified class (e.g. DeviceClass.Display)
    deviceClass?: DeviceClass;

    // Only return device types of the specified family (e.g. DeviceFamily.MatisseR5)
    deviceFamily?: DeviceFamily;
};

const defaultOptions = Object.freeze<Options>({
    refresh: false,
    deviceClass: undefined,
    deviceFamily: undefined,
});

export function useDeviceTypes({ refresh, deviceClass, deviceFamily }: Options = defaultOptions) {
    const dispatch = useDispatch();
    const { user } = useSession();
    const isLoggedIn = !!user;
    const [ reload, setReload ] = useState(refresh);
    const isLoadingDeviceTypes = useSelector(getIsLoadingDeviceTypes);
    const deviceTypes = useSelector(getDeviceTypes);
    const deviceTypesError = useSelector(getDeviceTypesError);

    // Load device types if we don't have them yet
    // TODO: have some provision where we'll retry to load them again if there was an error
    //       ...but need to build in a delay so we don't hammer the server on repeated errors.
    useEffect(() => {
        if (isLoggedIn && (reload || (!isLoadingDeviceTypes && (!deviceTypes || Object.keys(deviceTypes).length === 0) && !deviceTypesError))) {
            setReload(false);
            dispatch(getDeviceTypesRequest());
        }
    }, [ reload, dispatch, isLoadingDeviceTypes, deviceTypes, deviceTypesError, setReload, isLoggedIn ]);

    const deviceTypeOrder = useMemo(() => {
        return Object.values(deviceTypes)
            .filter(dt => typeof deviceClass === 'undefined' || dt.deviceClass === deviceClass)
            .filter(dt => typeof deviceFamily === 'undefined' || dt.deviceFamily === deviceFamily)
            .sort(sortNamesBy(dt => dt.displayName))
            .map(dt => dt.id);
    }, [ deviceTypes ]);

    return {
        deviceTypes,
        deviceTypeOrder,
        isLoadingDeviceTypes,
        deviceTypesError,
    };
}
